import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { grantAnalyticsConsent, denyAnalyticsConsent } from '@/lib/analytics';

const STORAGE_KEY = 'calcglobe-cookie-consent';

/**
 * Reads current consent. Returns:
 *   { state: 'accepted' | 'rejected', date: ISO string } | null
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Helper for other modules (e.g. analytics) to know if non-essential cookies are allowed. */
export function hasAcceptedCookies() {
  const c = getCookieConsent();
  return c?.state === 'accepted';
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show on first render only if consent hasn't been recorded yet
    const existing = getCookieConsent();
    if (!existing) {
      // Delay slightly so it doesn't flash on page load — feels less intrusive
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  // Also listen for an explicit "Manage cookies" action elsewhere in the app
  useEffect(() => {
    const onOpen = () => setVisible(true);
    window.addEventListener('calcglobe:open-cookie-banner', onOpen);
    return () => window.removeEventListener('calcglobe:open-cookie-banner', onOpen);
  }, []);

  const record = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, date: new Date().toISOString() }));
    } catch {}
    if (state === 'accepted') grantAnalyticsConsent();
    else denyAnalyticsConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      data-testid="cookie-consent-banner"
      className="fixed bottom-3 inset-x-3 sm:inset-x-auto sm:right-4 sm:left-4 md:left-auto md:max-w-md z-50 rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-xl p-4 sm:p-5 animate-fade-in"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-8 h-8 rounded-md bg-[#1A8FE3]/10 text-[#1A8FE3] flex items-center justify-center" aria-hidden="true">
          <Cookie className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 id="cookie-consent-title" className="font-chivo font-bold text-sm sm:text-base text-foreground mb-1">
            We use cookies
          </h2>
          <p id="cookie-consent-desc" className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            CalcGlobe uses essential cookies to make the site work. With your consent we may also use cookies for analytics and ads to keep the site free.{' '}
            <Link to="/cookie-policy" className="text-[#1A8FE3] hover:underline" data-testid="cookie-policy-link">
              Read our Cookie Policy
            </Link>.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              size="sm"
              onClick={() => record('accepted')}
              className="bg-[#1A8FE3] hover:bg-[#1577C0] text-white text-xs h-8"
              data-testid="cookie-accept-btn"
            >
              Accept all
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => record('rejected')}
              className="text-xs h-8"
              data-testid="cookie-reject-btn"
            >
              Reject non-essential
            </Button>
          </div>
        </div>
        <button
          onClick={() => record('rejected')}
          className="shrink-0 text-muted-foreground hover:text-foreground p-1 -m-1"
          aria-label="Close cookie banner (reject non-essential cookies)"
          data-testid="cookie-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
